import { z } from "zod";
import { defineStepConfig } from "../../registry/helpers";
import { WorkflowStepBatchConfigSchema } from "../../types/batch";
import {
  DATE_ONLY_INPUT,
  INTEGER_INPUT,
  OPTIONAL_DATE_ONLY_INPUT,
} from "../../types/socketInputs";
import { QUANTITY_INPUT } from "./quantityInput";

/**
 * Upsert Sales Order Line
 *
 * Takes ONE sales-order LINE as single-value input sockets, with its header's
 * fields alongside, shaped after the Gently model (`ar_sales_orders` +
 * `ar_sales_order_line_items`) — not after any source system. The service
 * groups lines into orders and writes both halves; a CDC observer or file
 * parser maps its own columns into these sockets, and anything source-specific
 * (status codes, sentinels, field naming) belongs in that upstream mapping.
 *
 * Flat, not nested, because that is the shape sources actually deliver: AS400
 * DMDTRANS, a CSV export and a CDC feed all emit one record per line with the
 * order's columns repeated on it.
 *
 * The replacement for the retired `transform_dmdtrans_sales_orders` step,
 * which read the `rexel_dmdtrans` landing table directly. This step reaches
 * the same two tables from mapped, source-agnostic rows.
 *
 * A leaf step: per-row outputs are execution results, not a downstream data
 * contract.
 */
export const upsertSalesOrderLineStep = defineStepConfig({
  type: "upsert_sales_order_line",
  name: "Upsert Sales Order Line",
  description:
    "Upserts one sales-order line and its order header (order/line origin ids, warehouse code, product SKU, dates, status, quantity, price). Resolves the warehouse and product, then writes both tables in source-agnostic batches. Fan a trigger's per-field array outputs into its single-value inputs and add a batch config to process rows in chunks.",
  category: "Transformation",
  libraryModule: "Procurement",

  inputSchema: z.object({
    // Nullable for the same reason as the other upsert steps: input is
    // validated BEFORE the handler runs, a parse failure is non-retryable, and
    // the batch path shares one activity per chunk — so a non-nullable socket
    // would fail a whole chunk of good rows over a few junk ones. A missing
    // value is simply a row this step reports as unwritable.
    orderOriginId: z
      .string()
      .nullable()
      .describe(
        "The ORDER's natural key in the source system; null is unresolvable",
      ),
    // The load-bearing socket. `ar_sales_order_line_items` has no other natural
    // key: measured on 9.99M production-shaped rows,
    // (sales_order, line_item_number) carries 28,642 duplicate groups and is
    // NULL on 99% of them, so it cannot serve as a conflict target. Without a
    // per-line id a re-ingest cannot update what it wrote last time and would
    // duplicate it instead.
    lineOriginId: z
      .string()
      .nullable()
      .describe(
        "The LINE's natural key, unique per line — not per order. Must distinguish lines an order repeats (AS400 reuses a line number across ship-from locations). Null is unresolvable.",
      ),
    warehouseCode: z
      .string()
      .nullable()
      .describe(
        "Warehouse origin code (resolved to the warehouse); null is unresolvable",
      ),
    productSku: z
      .string()
      .nullable()
      .describe(
        "Product SKU (resolved to the product). An unknown or missing SKU does NOT drop the line — it lands with no product reference and is reported, so one bad item code cannot take an order's other lines down with it.",
      ),
    // A validated date rather than `z.string()`: the value reaches a DATE bind
    // directly, and `ar_sales_orders.order_date` is NOT NULL, so a malformed
    // value would fail the whole chunk's INSERT. Validating at the boundary
    // keeps the check out of the internal service. DATE_ONLY_INPUT also accepts
    // the slash-separated spelling DMDTRANS emits (`2026/07/08`), which would
    // otherwise silently drop every line.
    orderDate: DATE_ONLY_INPUT.nullable().describe(
      "Order date, YYYY-MM-DD. Required — null is unresolvable.",
    ),
    deliveryDate: OPTIONAL_DATE_ONLY_INPUT.describe(
      "Requested delivery date, YYYY-MM-DD",
    ),
    shipDate: OPTIONAL_DATE_ONLY_INPUT.describe("Ship date"),
    // Deliberately our vocabulary, not the source's. Decoding '00'/'BO'/'SH'
    // is the mapping's job — a step that guessed would bury per-source rules
    // in shared code. An unrecognised value is reported, never coerced: the
    // replenishment engine only counts 'pending'/'in_progress' as live demand,
    // so a silent fallback would invent or erase demand.
    // A plain string, NOT z.enum, for the same reason every identifier socket
    // here is nullable: input is validated BEFORE the handler runs, a parse
    // failure is non-retryable, and the batch path shares one activity per
    // chunk — so an enum would let ONE unmapped status abort a whole chunk of
    // good rows. The vocabulary check lives in `upsertSalesOrdersFromRows`,
    // which reports the row as `invalidRow` and writes the rest.
    status: z
      .string()
      .nullable()
      .describe(
        "Order status in Gently's vocabulary — pending, in_progress, fulfilled or closed. Map source codes upstream; anything else is reported per row. Only pending/in_progress count as live demand.",
      ),
    // INTEGER_INPUT, not a bare z.number().int(): ingest foreign-table columns
    // are TEXT, so a socket wired straight to one delivers "3". The parse runs
    // before the handler and is non-retryable, so one string here failed the
    // whole 5,000-row chunk — measured on the DMDTRANS backfill, where every
    // chunk died on gdlineno.
    lineItemNumber: INTEGER_INPUT.nullable()
      .optional()
      .describe("Line number as shown on the order. Display only — not a key."),
    quantity: QUANTITY_INPUT.nullable().describe("Ordered quantity"),
    // Folded into line metadata with the line's ship date (the `shipDate`
    // socket), because demand history is fitted on what shipped and when, not
    // on what was ordered: an open order counts at its full size otherwise.
    shippedQuantity: QUANTITY_INPUT.nullable()
      .optional()
      .describe(
        "Quantity shipped on this line (AS400 GDSHPQTY), folded into line metadata as `shippedQuantity`, together with the `shipDate` value as `shipDate`. The forecaster fits on this and buckets it by that ship date instead of the ordered quantity and order date. Null stores nothing; 0 is a real unshipped line.",
      ),
    unitPrice: QUANTITY_INPUT.nullable().optional().describe("Unit price"),
    // These two are scalar sockets, not part of `lineMetadata`, because the
    // graph cannot build them into an object: no registered socket transform
    // returns one. They carry the SOURCE's codes verbatim (unlike `status`,
    // which is mapped upstream) — the engine's filters are written against the
    // raw AS400 vocabulary and fail OPEN when the key is absent, so dropping
    // them silently counts closed lines and vendor POs as live demand.
    orderType: z
      .string()
      .nullable()
      .optional()
      .describe(
        "Source order-type code, folded into line metadata as `orderType`. '1' = vendor PO — the engine excludes it from sales demand; '3' = product-on-the-fly (also sets `isProductOnTheFly`). Null/blank stores nothing.",
      ),
    lineStatus: z
      .string()
      .nullable()
      .optional()
      .describe(
        "Source per-LINE status code, folded into line metadata as `lineStatus`. '00' = open; the engine treats anything else as closed/fulfilled and a missing value as open. Per line, not per order — orders mix statuses.",
      ),
    excludeFlag: z
      .string()
      .nullable()
      .optional()
      .describe(
        "Source exclude-from-forecast flag (AS400 GDEXCFLAG), folded into line metadata as `excludeFlag`. '1' = the planner excluded this line from demand history (a type-E keyed order), so the forecaster must not fit on it; '0' = considered and kept. Null/blank stores nothing. Dropping this silently trains the forecast on one-off project orders the source deliberately excluded.",
      ),
    lineMetadata: z
      .record(z.string(), z.unknown())
      .nullable()
      .optional()
      .describe(
        "Stored on the line. The engine reads `lineStatus` ('00' = open), `orderType` ('1' = vendor PO, excluded from demand), `excludeFlag` ('1' = excluded from forecast history) and `shippedQuantity` / `shipDate` (demand history) from here; wire the dedicated sockets for those. Keys given here explicitly win over the socket values.",
      ),
    // Same reason as orderType/lineStatus above: scalar sockets because the
    // graph cannot assemble an object. These identify the CUSTOMER the order
    // belongs to, which is the key the board joins on to read that customer's
    // credit hold — without them an order cannot be attributed to a customer.
    soldToCode: z
      .string()
      .nullable()
      .optional()
      .describe(
        "Source sold-to customer code (AS400 `gdsoldn`), folded into order metadata as `soldToCode`. Identifies the customer for credit-hold matching. Null/blank stores nothing.",
      ),
    customerName: z
      .string()
      .nullable()
      .optional()
      .describe(
        "Source customer name (AS400 `gdcname`), folded into order metadata as `customerName`. Display only.",
      ),
    // The per-division source tables carry no division column — one table IS
    // one division — so the division comes from which link changed, exactly as
    // `upsert_vendor_rexel` derives it. See `resolveDataSourceDivisions`.
    dataSourceLink: z
      .object({
        id: z.string().uuid().nullable().optional(),
        dataSourceId: z.string().uuid().nullable().optional(),
      })
      .nullable()
      .optional()
      .describe(
        "The observed Data Source Link. Supplies the division (its DataSource's title), folded into order metadata as `division`. Wire it from the CDC trigger.",
      ),
    orderMetadata: z
      .record(z.string(), z.unknown())
      .nullable()
      .optional()
      .describe(
        "Stored on the order header as origin_metadata. Keys given here win over the `soldToCode` / `customerName` / division socket values.",
      ),
  }),

  outputSchema: z.object({
    written: z.boolean().describe("The order and line were upserted"),
    skipped: z
      .boolean()
      .describe(
        "The row was not written: an unresolvable warehouse, or a missing key/date/status. An unknown product alone does not skip a row.",
      ),
    unresolvedProduct: z
      .boolean()
      .describe(
        "No product matched the SKU. The line was still written, without a product reference — check `written`, not this, to know whether it landed.",
      ),
    unresolvedWarehouse: z
      .boolean()
      .describe("No warehouse matched the warehouse code"),
    invalidRow: z
      .boolean()
      .describe(
        "Missing order/line origin id or order date, or an unmapped status",
      ),
  }),

  configSchema: z.object({
    batch: WorkflowStepBatchConfigSchema.prefault({ size: 5000 }),
    // Trimmed and non-empty: this value goes verbatim into both natural keys
    // and into warehouse resolution. A blank or padded one would key rows on
    // "" (or " as400 ") and quietly never match again on the next run.
    originSystem: z
      .string()
      .trim()
      .min(1)
      .describe(
        "Stamped on the order and line, and half of each natural key. Also restricts WAREHOUSE resolution to references from this origin system. Does not scope products: ProductV3 is keyed by SKU within the organization.",
      ),
    originEntity: z
      .string()
      .optional()
      .describe(
        "Stamped on the order header for provenance, e.g. the source table name. Not part of any key.",
      ),
  }),

  emits: [] as const,

  execution: {
    startToCloseTimeout: "30 minutes",
    retry: { maximumAttempts: 2 },
  },
});
